import React from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { mpdCardSx } from './portfolioMuiTheme';

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

function KpiTile({
  icon, label, value, sub, accent = '#9fe870', valueTitle, wide,
}) {
  const valueNode = (
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        lineHeight: 1.2,
        mt: 0.25,
        fontSize: wide ? { xs: '0.95rem', sm: '1.1rem', md: '1.2rem' } : '1.05rem',
        whiteSpace: wide ? 'normal' : 'nowrap',
        wordBreak: wide ? 'break-word' : undefined,
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Paper
      component={motion.div}
      variants={fadeUp}
      elevation={0}
      className={`mpd-mui-kpi${wide ? ' mpd-mui-kpi--wide' : ''}`}
      sx={(theme) => ({
        ...mpdCardSx(theme),
        p: { xs: 1.75, sm: 2.25 },
        height: '100%',
        minWidth: wide ? { xs: '100%', sm: 200 } : 0,
      })}
    >
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: `${accent}18`,
            color: accent,
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          {valueTitle ? (
            <Tooltip title={valueTitle} placement="top" arrow>
              {valueNode}
            </Tooltip>
          ) : valueNode}
          {sub && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default function PortfolioAnalyticsKpi({ items }) {
  const wideCount = items.filter((i) => i.wide).length;
  const regularCount = items.length - wideCount;

  return (
    <Box
      component={motion.div}
      variants={stagger}
      initial="hidden"
      animate="show"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: wideCount > 0
            ? `minmax(280px, 1.4fr) repeat(${Math.max(regularCount, 1)}, minmax(160px, 1fr))`
            : `repeat(${items.length}, minmax(180px, 1fr))`,
        },
        gap: { xs: 1.25, sm: 1.75 },
        minWidth: 0,
      }}
    >
      {items.map((item) => (
        <KpiTile key={item.key} {...item} />
      ))}
    </Box>
  );
}
